/**
 * Lazy routes that survive a deploy.
 *
 * THE BUG THIS EXISTS TO FIX
 * --------------------------
 * A tab left open across a deploy died on the next navigation with
 *
 *   Failed to fetch dynamically imported module:
 *   https://bdi.internal.photonsmart.tech/assets/Login-D2lT4FZj.js
 *
 * Every route is `React.lazy`, so its code arrives as a separate chunk whose
 * filename carries a content hash. A deploy replaces the container image, the
 * old hashes stop existing, and `/assets/Login-D2lT4FZj.js` returns a real 404
 * — nginx correctly refuses to serve index.html for /assets/*, so the browser
 * gets HTML where it demanded a module and rejects the import.
 *
 * The open tab is running an application that is no longer deployed. Nothing
 * in it can be repaired in place; the only way forward is to fetch the new one.
 *
 * WHY IT RELOADS INSTEAD OF REPORTING
 * -----------------------------------
 * An error screen with a Reload button asks the user to do the one thing we
 * already know is required. `location.reload()` keeps the URL, so they land on
 * the page they were trying to open, on the current build.
 *
 * WHY IT ONLY DOES THAT ONCE
 * --------------------------
 * A reload loop is worse than an error — it is an error you cannot read. If a
 * chunk still fails after we have just reloaded, the cause is not staleness
 * (offline, a broken deploy, a blocked CDN) and the route error boundary should
 * say so. The guard lives in `sessionStorage` because the reload wipes every
 * module-level variable we could otherwise have used to remember.
 */

import { lazy, type ComponentType } from 'react'

const RELOAD_MARKER = 'bdi:chunk-reload-at'

/**
 * How recently a reload has to have happened to count as "we already tried".
 *
 * Long enough to cover a slow reload on a poor connection, short enough that a
 * genuinely stale tab opened hours later still gets its one free recovery.
 */
const RECENT_RELOAD_MS = 30_000

/**
 * Whether this looks like a chunk that is no longer on the server, rather than
 * a module that threw while evaluating.
 *
 * Matched on message because browsers disagree on the error type: Chrome and
 * Safari reject with a TypeError, Firefox with a plain Error, and none of them
 * expose a machine-readable code.
 */
export function isChunkLoadError(error: unknown): boolean {
  const message =
    error instanceof Error ? error.message : typeof error === 'string' ? error : ''
  return (
    /failed to fetch dynamically imported module/i.test(message) ||
    /error loading dynamically imported module/i.test(message) ||
    /importing a module script failed/i.test(message) ||
    // Vite's preload helper, and Webpack's equivalent for good measure.
    /failed to (?:fetch|load) module script/i.test(message) ||
    /loading chunk \d+ failed/i.test(message)
  )
}

/**
 * Claims this tab's one recovery reload. Returns false if it has already been
 * spent recently, or if storage is unavailable (private browsing) — in which
 * case we would have no way to stop looping, so we do not start.
 */
export function claimReload(now: number = Date.now()): boolean {
  try {
    const last = Number(sessionStorage.getItem(RELOAD_MARKER) ?? '0')
    if (Number.isFinite(last) && now - last < RECENT_RELOAD_MS) return false
    sessionStorage.setItem(RELOAD_MARKER, String(now))
    return true
  } catch {
    return false
  }
}

/** Recovers from a stale chunk once, or rethrows so the error boundary shows. */
export function recoverFromStaleChunk<T>(error: unknown): Promise<T> {
  if (isChunkLoadError(error) && claimReload()) {
    window.location.reload()
    // Never resolves. The document is being replaced, and resolving — with
    // anything, including a rethrow — would flash a failure the user cannot
    // act on in the instant before navigation.
    return new Promise<T>(() => {})
  }
  throw error
}

/**
 * `React.lazy`, but a chunk missing because of a deploy reloads instead of
 * killing the route. Signature-compatible, so it is a drop-in at every callsite.
 */
export function lazyWithReload<T extends ComponentType<unknown>>(
  factory: () => Promise<{ default: T }>,
) {
  return lazy(() => factory().catch(recoverFromStaleChunk<{ default: T }>))
}

/**
 * Vite fires `vite:preloadError` when a `<link rel=modulepreload>` fails, which
 * happens BEFORE any import() rejects and so never reaches the wrapper above.
 * Same staleness, same recovery.
 */
export function installPreloadErrorRecovery(): void {
  window.addEventListener('vite:preloadError', (event) => {
    if (claimReload()) {
      // Stop Vite from rethrowing into an unhandled rejection we are about to
      // navigate away from anyway.
      event.preventDefault()
      window.location.reload()
    }
  })
}
